import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api from "@/lib/api";
import { Loader2, ShieldCheck, Eye, EyeOff, ArrowRight } from "lucide-react";

export default function ResetPassword() {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const initialEmail = params.get("email") || "";
    const [step, setStep] = useState(1); // 1 = enter code, 2 = new password
    const [email, setEmail] = useState(initialEmail);
    const [code, setCode] = useState("");
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [showPwd, setShowPwd] = useState(false);
    const [loading, setLoading] = useState(false);
    const [resending, setResending] = useState(false);

    // A tiny helper for OTP input UX — normalize to digits only, cap at 6 chars.
    const setCodeSafe = (v) => setCode((v || "").replace(/\D/g, "").slice(0, 6));

    const passwordsMatch = useMemo(() => password === confirm, [password, confirm]);
    const passwordValid = useMemo(() => password.length >= 8, [password]);

    const verifyCode = async (e) => {
        e.preventDefault();
        if (code.length !== 6) return toast.error("Enter the 6-digit code");
        setLoading(true);
        try {
            await api.post("/auth/verify-reset-code", { email, code });
            toast.success("Code verified");
            setStep(2);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Invalid or expired code");
        } finally {
            setLoading(false);
        }
    };

    const resend = async () => {
        if (!email) return toast.error("Enter your email first");
        setResending(true);
        try {
            await api.post("/auth/forgot-password", { email });
            toast.success("A fresh code is on its way");
        } catch (e) {
            toast.error("Could not resend — try again");
        } finally {
            setResending(false);
        }
    };

    const finalize = async (e) => {
        e.preventDefault();
        if (!passwordValid) return toast.error("Password must be at least 8 characters");
        if (!passwordsMatch) return toast.error("Passwords don't match");
        setLoading(true);
        try {
            await api.post("/auth/reset-password", { email, code, new_password: password });
            toast.success("Password updated. Please sign in.");
            navigate("/login");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not reset password");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="max-w-md mx-auto px-6 py-2" data-testid="reset-password-page">
            <div className="rounded-2xl border border-border bg-card p-8">
                <div className="h-12 w-12 rounded-full bg-[hsl(var(--accent))]/15 border border-[hsl(var(--accent))]/40 flex items-center justify-center mb-5">
                    <ShieldCheck className="h-5 w-5 text-[hsl(var(--accent))]" />
                </div>

                {step === 1 ? (
                    <>
                        <h1 className="font-display text-4xl">Enter your code.</h1>
                        <p className="text-muted-foreground mt-2 text-sm">
                            We sent a 6-digit code to <span className="text-foreground">{email || "your inbox"}</span>. It expires in 15 minutes.
                        </p>
                        <form onSubmit={verifyCode} className="space-y-4 mt-7">
                            {!initialEmail && (
                                <div>
                                    <Label className="font-mono-ui mb-2 block">Email</Label>
                                    <Input
                                        type="email"
                                        required
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="rounded-2xl bg-background/60 border-border h-11"
                                        data-testid="reset-email-input"
                                    />
                                </div>
                            )}
                            <div>
                                <Label className="font-mono-ui mb-2 block">6-digit code</Label>
                                <Input
                                    type="text"
                                    inputMode="numeric"
                                    autoComplete="one-time-code"
                                    required
                                    value={code}
                                    onChange={(e) => setCodeSafe(e.target.value)}
                                    className="rounded-2xl bg-background/60 border-border h-14 text-center text-2xl tracking-[0.6em] font-mono-ui"
                                    data-testid="reset-code-input"
                                    placeholder="000000"
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={loading || code.length !== 6}
                                className="w-full dw-btn-primary h-12 font-mono-ui flex items-center justify-center gap-2 disabled:opacity-60"
                                data-testid="reset-verify-button"
                            >
                                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
                                {loading ? "Verifying…" : "Verify code"}
                            </button>
                        </form>
                        <div className="mt-6 flex items-center justify-between text-sm">
                            <Link to="/login" className="text-muted-foreground underline underline-offset-4" data-testid="reset-back-login">
                                Back to sign in
                            </Link>
                            <button
                                type="button"
                                onClick={resend}
                                disabled={resending}
                                className="text-foreground underline underline-offset-4 disabled:opacity-50"
                                data-testid="reset-resend-code"
                            >
                                {resending ? "Sending…" : "Resend code"}
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <h1 className="font-display text-4xl">Set a new password.</h1>
                        <p className="text-muted-foreground mt-2 text-sm">Choose something you'll remember — at least 8 characters.</p>
                        <form onSubmit={finalize} className="space-y-4 mt-7">
                            <div>
                                <Label className="font-mono-ui mb-2 block">New password</Label>
                                <div className="relative">
                                    <Input
                                        type={showPwd ? "text" : "password"}
                                        required
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="rounded-2xl bg-background/60 border-border h-11 pr-10"
                                        data-testid="reset-new-password-input"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPwd((s) => !s)}
                                        className="absolute inset-y-0 right-3 flex items-center text-muted-foreground"
                                        aria-label={showPwd ? "Hide password" : "Show password"}
                                    >
                                        {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                    </button>
                                </div>
                                {!passwordValid && password.length > 0 && (
                                    <p className="text-xs text-[hsl(var(--accent))] mt-2">At least 8 characters, please.</p>
                                )}
                            </div>
                            <div>
                                <Label className="font-mono-ui mb-2 block">Confirm password</Label>
                                <Input
                                    type={showPwd ? "text" : "password"}
                                    required
                                    value={confirm}
                                    onChange={(e) => setConfirm(e.target.value)}
                                    className="rounded-2xl bg-background/60 border-border h-11"
                                    data-testid="reset-confirm-password-input"
                                />
                                {confirm && !passwordsMatch && (
                                    <p className="text-xs text-[hsl(var(--accent))] mt-2">Passwords don't match.</p>
                                )}
                            </div>
                            <button
                                type="submit"
                                disabled={loading || !passwordValid || !passwordsMatch}
                                className="w-full dw-btn-primary h-12 font-mono-ui flex items-center justify-center gap-2 disabled:opacity-60"
                                data-testid="reset-finalize-button"
                            >
                                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
                                {loading ? "Saving…" : "Update password"}
                            </button>
                        </form>
                    </>
                )}
            </div>
            <div className="h-24" />
        </div>
    );
}
